import { RadioButton } from '@pay-com/components'
import {
  Controller,
  FieldPath,
  FieldValues,
  useFormContext
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getErrorMessage } from 'utils/get-error-message'

interface ControlledRadioProps<T extends FieldValues> {
  name: FieldPath<T>
  label?: string
  options: { value: string | boolean; label: string }[]
  disabled?: boolean
  isLoading?: boolean
  className?: string
}

export const ControlledRadio = <T extends FieldValues>({
  name,
  label,
  options,
  disabled,
  isLoading,
  className
}: ControlledRadioProps<T>) => {
  const { control } = useFormContext<T>()
  const { t } = useTranslation('errors')

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { error } = fieldState

        return (
          <div className={className}>
            {label && (
              <span className="mb-1 block text-sm font-medium">{label}</span>
            )}
            <div className="flex gap-4">
              {options.map(option => (
                <RadioButton
                  key={`${option.value} ${option.label}`}
                  name={name}
                  value={String(option.value)}
                  label={option.label}
                  checked={field.value === option.value}
                  onChange={() => field.onChange(option.value)}
                  disabled={disabled || isLoading}
                  error={Boolean(error)}
                />
              ))}
            </div>
            {error?.message && (
              <span className="mt-1 text-xs text-red-500">
                {getErrorMessage(t, error?.message)}
              </span>
            )}
          </div>
        )
      }}
    />
  )
}
